import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Check, Circle, CreditCard, DollarSign, Globe, Loader2, RefreshCw, Users } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  connectStripeAccount,
  getStripeOAuthUrl,
  getStripeOnboardingLink,
  openStripeOnboardingPopup,
  STRIPE_CONNECT_COMPLETE,
} from "@/lib/stripe-connect"
import { supabase } from "@/lib/supabase"
import { toast } from "sonner"
import { usePermission } from "@/hooks/usePermission"
import { useOrgStore } from "@/stores/orgStore"
import { RequireOrg } from "@/components/RequireOrg"
import { formatCurrency, formatDate } from "@/lib/format"
import { errorMessage } from "@/lib/errorMessage"
import { ImportStatusCard } from "@/components/dashboard/ImportStatusCard"
import { PostTransferBanner } from "@/components/dashboard/PostTransferBanner"
import { FundRequestsCard } from "@/components/dashboard/FundRequestsCard"
import { StripeContinuityCard } from "@/components/dashboard/StripeContinuityCard"
import type { Tables } from "@/types/database"

type Donation = Pick<
  Tables<"donations">,
  "id" | "amount" | "contact_id" | "donor_name" | "donor_email" | "created_at"
>

export function DashboardPage() {
  return (
    <RequireOrg>
      <DashboardPageContent />
    </RequireOrg>
  )
}

function DashboardPageContent() {
  const { currentOrg, userOrgs, fetchUserOrgs } = useOrgStore()
  const { hasPermission: canViewFinancials, loading: permissionsLoading } =
    usePermission("can_view_financials")
  const [stripeLoading, setStripeLoading] = useState(false)
  const [stripeError, setStripeError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [websitePublished, setWebsitePublished] = useState<boolean | null>(null)
  const [donations, setDonations] = useState<Donation[]>([])
  const [donationStats, setDonationStats] = useState({
    totalCents: 0,
    count: 0,
    uniqueContacts: 0,
  })
  const [donationsLoading, setDonationsLoading] = useState(true)

  const orgId = (currentOrg ?? userOrgs[0])?.id
  useEffect(() => {
    if (!orgId) return
    let cancelled = false
    supabase
      .from("pages")
      .select("is_published")
      .eq("org_id", orgId)
      .eq("slug", "home")
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled && data) {
          setWebsitePublished((data as { is_published: boolean }).is_published)
        }
      })
    return () => {
      cancelled = true
    }
  }, [orgId])

  useEffect(() => {
    if (!canViewFinancials && !permissionsLoading) {
      setDonations([])
      setDonationStats({ totalCents: 0, count: 0, uniqueContacts: 0 })
      setDonationsLoading(false)
      return
    }
    if (!orgId || !canViewFinancials) return
    setDonationsLoading(true)
    let cancelled = false
    supabase
      .from("donations")
      .select("id, amount, contact_id, donor_name, donor_email, created_at")
      .eq("org_id", orgId)
      .eq("status", "succeeded")
      .order("created_at", { ascending: false })
      .limit(1000)
      .then(({ data, error }) => {
        if (cancelled) return
        setDonationsLoading(false)
        if (error) {
          console.error("Donations fetch error:", error)
          return
        }
        const list = (data ?? []) as Donation[]
        setDonations(list)
        const totalCents = list.reduce((sum, d) => sum + d.amount, 0)
        const uniqueContacts = new Set(
          list.map((d) => d.contact_id ?? d.donor_email).filter(Boolean)
        ).size
        setDonationStats({
          totalCents,
          count: list.length,
          uniqueContacts,
        })
      })
    return () => {
      cancelled = true
    }
  }, [orgId, canViewFinancials])

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      if (event.data?.type !== STRIPE_CONNECT_COMPLETE) return
      console.log("[DashboardPage] Stripe Connect completed, refreshing org data...")
      fetchUserOrgs()
      if (orgId) {
        supabase
          .from("pages")
          .select("is_published")
          .eq("org_id", orgId)
          .eq("slug", "home")
          .maybeSingle()
          .then(({ data }) => {
            if (data) setWebsitePublished((data as { is_published: boolean }).is_published)
          })
      }
    }
    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [orgId, fetchUserOrgs])

  const org = currentOrg ?? userOrgs[0]
  const stripeConnected = org.stripe_onboarding_complete === true
  const isPublished = websitePublished === true

  const handleConnectStripe = async () => {
    setStripeError(null)
    setStripeLoading(true)
    try {
      const { error: createError } = await connectStripeAccount(org.id)
      if (createError) {
        setStripeError(createError)
        setStripeLoading(false)
        return
      }
      const returnBaseUrl = typeof window !== "undefined" ? window.location.origin : undefined
      const { data, error: linkError } = await getStripeOnboardingLink(org.id, returnBaseUrl)
      if (linkError || !data?.url) {
        setStripeError(linkError ?? "Could not get onboarding link")
        setStripeLoading(false)
        return
      }
      openStripeOnboardingPopup(data.url)
    } catch (err) {
      setStripeError(errorMessage(err))
    } finally {
      setStripeLoading(false)
    }
  }

  // NOS-194: connect an EXISTING Stripe account via OAuth. The popup lands on
  // /stripe/connect/callback, which exchanges code+state server-side and
  // notifies this page via STRIPE_CONNECT_COMPLETE.
  const handleConnectExistingStripe = async () => {
    setStripeError(null)
    setStripeLoading(true)
    try {
      const returnBaseUrl = typeof window !== "undefined" ? window.location.origin : undefined
      const { data, error: authorizeError } = await getStripeOAuthUrl(org.id, returnBaseUrl)
      if (authorizeError || !data?.url) {
        setStripeError(authorizeError ?? "Could not start the Stripe connection")
        return
      }
      openStripeOnboardingPopup(data.url)
    } catch (err) {
      setStripeError(errorMessage(err))
    } finally {
      setStripeLoading(false)
    }
  }

  const handleRefreshStripeStatus = async () => {
    if (!org?.id) return

    setRefreshing(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error("Not authenticated")

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-stripe-status`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ org_id: org.id }),
        }
      )

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || "Failed to check status")
      }

      const result = await response.json()

      await fetchUserOrgs()

      if (result.onboarding_complete) {
        toast.success("Stripe onboarding is complete!")
      } else {
        toast.info(
          "Stripe onboarding is still pending. Complete all steps in Stripe Dashboard."
        )
      }
    } catch (error) {
      console.error("Failed to refresh Stripe status:", error)
      toast.error("Failed to check Stripe status. Please try again.")
    } finally {
      setRefreshing(false)
    }
  }
 
  return (
    <div className="bg-background">
      <PostTransferBanner orgId={org.id} />
      {/* NOS-226: background-import status — running / review / failed / done */}
      <ImportStatusCard orgId={org.id} orgSlug={org.slug} />
      <section className="mb-8">
        <h2 className="sr-only">Stats</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Donations</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {!canViewFinancials && !permissionsLoading ? (
                  <p className="text-sm text-muted-foreground">
                    You don&apos;t have permission to view financial data
                  </p>
                ) : donationsLoading || permissionsLoading ? (
                  <>
                    <div className="h-8 w-24 animate-pulse rounded bg-muted" />
                    <div className="mt-1 h-4 w-32 animate-pulse rounded bg-muted" />
                  </>
                ) : donationStats.count === 0 ? (
                  <>
                    <p className="text-2xl font-bold">$0</p>
                    <p className="text-xs text-muted-foreground">No donations yet</p>
                  </>
                ) : (
                  <>
                    <p className="text-2xl font-bold">
                      {formatCurrency(donationStats.totalCents, { digits: 0 })}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      from {donationStats.uniqueContacts} contact
                      {donationStats.uniqueContacts !== 1 ? "s" : ""}
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Contacts</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {!canViewFinancials && !permissionsLoading ? (
                  <p className="text-sm text-muted-foreground">
                    You don&apos;t have permission to view donation data
                  </p>
                ) : donationsLoading || permissionsLoading ? (
                  <>
                    <div className="h-8 w-16 animate-pulse rounded bg-muted" />
                    <div className="mt-1 h-4 w-32 animate-pulse rounded bg-muted" />
                  </>
                ) : (
                  <>
                    <p className="text-2xl font-bold">{donationStats.uniqueContacts}</p>
                    <p className="text-xs text-muted-foreground">
                      {donationStats.count === 0
                        ? "Start accepting donations"
                        : `${donationStats.count} donation${donationStats.count !== 1 ? "s" : ""} received`}
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Website Status</CardTitle>
                <Globe className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isPublished ? (
                  <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                    Published
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-md bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20">
                    Draft
                  </span>
                )}
                <div className="mt-3 flex items-center gap-2 text-sm">
                  <CreditCard
                    className={`h-4 w-4 shrink-0 ${stripeConnected ? "text-green-600" : "text-muted-foreground"}`}
                  />
                  {stripeConnected ? (
                    <span className="font-medium text-green-700">Connected</span>
                  ) : (
                    <Link
                      to="/dashboard/settings"
                      className="font-medium text-muted-foreground hover:text-foreground hover:underline"
                    >
                      Connect Stripe →
                    </Link>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {isPublished ? (
                    <Button size="sm" variant="outline" asChild>
                      <a href={`/${org.slug}`} target="_blank" rel="noopener noreferrer">
                        View site
                      </a>
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" disabled title="Publish your site first">
                      View site
                    </Button>
                  )}
                  <Button size="sm" variant="outline" asChild>
                    <Link to="/dashboard/builder">Edit site</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="mb-4 text-lg font-semibold">Recent Donations</h2>
          <Card>
            <CardContent className="p-0">
              {!canViewFinancials && !permissionsLoading ? (
                <p className="p-8 text-center text-muted-foreground">
                  You don&apos;t have permission to view donation details.
                </p>
              ) : donationsLoading ? (
                <div className="flex items-center justify-center gap-2 p-12">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Loading donations...</span>
                </div>
              ) : donations.length === 0 ? (
                <p className="p-8 text-center text-muted-foreground">
                  No donations yet. Share your website to start fundraising!
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {donations.slice(0, 10).map((d) => (
                      <TableRow key={d.id}>
                        <TableCell>{formatDate(d.created_at)}</TableCell>
                        <TableCell>{d.donor_name || d.donor_email || "—"}</TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(d.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </section>

        {(!stripeConnected || !isPublished) && (
          <section>
            <h2 className="mb-4 text-lg font-semibold">Get Started with NOS</h2>
            {stripeError && (
              <div
                className="mb-4 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
                role="alert"
              >
                {stripeError}
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-1">
              <Card className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-1 items-start gap-3 p-6">
                {stripeConnected ? (
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-green-600" aria-hidden />
                ) : (
                  <Circle className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
                )}
                <div>
                  <CardTitle className="text-base">
                    {stripeConnected ? "✓ Stripe Connected" : "Connect Stripe"}
                  </CardTitle>
                  <CardDescription>
                    {stripeConnected
                      ? "You can accept donations online."
                      : "⚠ Connect Stripe to accept donations"}
                  </CardDescription>
                </div>
              </div>
              <CardFooter className="border-t p-4 sm:border-t-0 sm:border-l sm:py-6">
                {stripeConnected ? (
                  <div className="flex gap-2">
                    <Button size="sm" disabled className="bg-green-600 text-white hover:bg-green-600">
                      ✓ Connected
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleRefreshStripeStatus}
                      disabled={refreshing}
                    >
                      {refreshing ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Checking...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="mr-2 h-4 w-4" />
                          Refresh Status
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleConnectStripe}
                      disabled={stripeLoading}
                    >
                      {stripeLoading ? (
                        <>
                          <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        "Start"
                      )}
                    </Button>
                    {org.stripe_account_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleRefreshStripeStatus}
                        disabled={refreshing}
                      >
                        {refreshing ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Checking...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="mr-2 h-4 w-4" />
                            Refresh Status
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                  {/* NOS-194: OAuth path for orgs that already run on Stripe */}
                  {!org.stripe_account_id && (
                    <button
                      type="button"
                      onClick={handleConnectExistingStripe}
                      disabled={stripeLoading}
                      className="text-left text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:opacity-50"
                    >
                      Already use Stripe? Connect your existing account
                    </button>
                  )}
                  </div>
                )}
              </CardFooter>
              </Card>
              <Card className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-1 items-start gap-3 p-6">
                  {isPublished ? (
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-green-600" aria-hidden />
                  ) : (
                    <Circle className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
                  )}
                  <div>
                    <CardTitle className="text-base">Customize your website</CardTitle>
                  <CardDescription>
                    {isPublished
                      ? "Your site is customized and live."
                      : "Add your mission, pages, and branding so supporters can learn about your work."}
                    </CardDescription>
                  </div>
                </div>
                <CardFooter className="border-t p-4 sm:border-t-0 sm:border-l sm:py-6">
                  <Button size="sm" variant="outline" asChild>
                    <Link to="/dashboard/builder">
                      {isPublished ? "Edit & update" : "Customize"}
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
              <Card className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-1 items-start gap-3 p-6">
                  {isPublished ? (
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-green-600" aria-hidden />
                  ) : (
                    <Circle className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
                  )}
                  <div>
                    <CardTitle className="text-base">
                      {isPublished ? "Your website is live" : "Publish your website"}
                    </CardTitle>
                    <CardDescription>
                      {isPublished
                        ? "Your website is live. Edit and republish from Customize."
                        : "Make your donation page live so supporters can find and give to you."}
                    </CardDescription>
                  </div>
                </div>
                <CardFooter className="border-t p-4 sm:border-t-0 sm:border-l sm:py-6">
                  {isPublished ? (
                    <Button size="sm" variant="outline" asChild>
                      <Link to="/dashboard/builder">Edit & update</Link>
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" asChild>
                      <Link to="/dashboard/builder">Publish</Link>
                    </Button>
                  )}
                </CardFooter>
              </Card>
            </div>
          </section>
        )}
      {/* NOS-194: OAuth-connected orgs can run donor discovery / disconnect */}
      {org.stripe_account_id && org.stripe_account_type === "standard" && (
        <section className="mb-8">
          <StripeContinuityCard orgId={org.id} onDisconnected={fetchUserOrgs} />
        </section>
      )}
      <FundRequestsCard orgId={org.id} />
    </div>
  )
}
